<template>
  <div class="data-review">
    <transition name="toast-fade">
      <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]" @click="toast.show = false">
        {{ toast.message }}
      </div>
    </transition>

    <div class="task-selector-bar">
      <div class="task-selector">
        <label>当前任务：</label>
        <select :value="selectedTaskId" @change="$emit('task-change', $event.target.value)" :disabled="isLoading || tasks.length === 0" :class="{ 'select-empty': tasks.length === 0 }">
          <option v-if="tasks.length === 0" value="" disabled>无任务</option>
          <option v-for="task in tasks" :key="task.id" :value="task.id">
            {{ formatCrawlerTaskLabel(task) }} ({{ task.report_period }}) - {{ getTaskStatusText(task.status) }}
          </option>
        </select>
        <span v-if="selectedTask" class="task-stats">
          共 {{ selectedTask.result_count || 0 }} 条 |
          有效 {{ selectedTask.valid_count || 0 }} 条
        </span>
      </div>
    </div>

    <div v-if="!selectedTaskId" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#c0c4cc" stroke-width="1.5">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
        </svg>
      </div>
      <p>请先选择一个任务来查看数据</p>
    </div>

    <div v-else-if="!isTaskParsed" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#c0c4cc" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
        </svg>
      </div>
      <p>该任务尚未完成PDF解析，请先到「爬取任务」面板启动解析</p>
    </div>

    <template v-else>
      <div v-if="enrichMissingOnPage > 0" class="enrich-alert">
        本页有 <strong>{{ enrichMissingOnPage }}</strong> 条仍缺部分参考字段（省/行业/实控人/市值等）。参考数据来自全局「公司信息」表，可前往维护或在编辑弹窗中填写。
      </div>
      <div v-if="selectedTaskId && isTaskParsed" class="listed-version-bar">
        <label class="listed-version-label" for="listed-version-select">参考表版本</label>
        <select
          v-if="listedDataVersions.length"
          id="listed-version-select"
          v-model="selectedListedInfoDataVersion"
          class="listed-version-select"
          @change="onListedVersionChange"
        >
          <option v-for="v in listedDataVersions" :key="v" :value="v">{{ v }}</option>
        </select>
        <span v-else-if="listedVersionApiFailed" class="listed-version-warn">
          无法加载版本列表（接口异常，常见原因：<strong>node 后端未重启</strong>，新版本路由未生效）。
          若本页已能带出参考字段，加载数据后会根据本页记录自动补全下拉选项。
        </span>
        <span v-else class="listed-version-none">暂无可用版本号：库中尚无公司信息参考表数据，或尚未导入并填写批次版本（如 25Q4）</span>
        <span class="listed-version-hint" v-if="listedDataVersions.length && listedCompanyInfoVersionLabel">
          公司信息版本来源：<strong>{{ listedCompanyInfoVersionLabel }}</strong>，默认使用最新版数据
        </span>
        <span v-else-if="listedDataVersions.length" class="listed-version-none">当前页无匹配参考行</span>
      </div>

      <div class="section-header">
        <div class="section-title-row">
          <h3>数据审核</h3>
          <div class="view-toggle" role="tablist" aria-label="视图切换">
            <button type="button" role="tab" :aria-selected="viewMode === 'compact'" :class="['btn-toggle', { active: viewMode === 'compact' }]" @click="viewMode = 'compact'">
              精简审核视图
            </button>
            <button type="button" role="tab" :aria-selected="viewMode === 'full'" :class="['btn-toggle', { active: viewMode === 'full' }]" @click="viewMode = 'full'">
              全列对照
            </button>
          </div>
        </div>
        <div class="filters">
          <label>人工检查：</label>
          <select v-model="filterAutoCheck" @change="loadResults">
            <option value="">全部</option>
            <option value="有数据,需核实">有数据 + 需核实</option>
            <option value="有数据">有数据</option>
            <option value="需核实">需核实</option>
            <option value="无数据">无数据</option>
          </select>
          <input
            type="text"
            v-model="searchKeyword"
            placeholder="搜索公司或证券代码..."
            @keyup.enter="loadResults"
            class="search-input"
          />
          <button class="btn btn-secondary btn-sm" @click="loadResults">搜索</button>
        </div>
      </div>

      <div class="data-tip" v-if="filterAutoCheck === '' || filterAutoCheck === '有数据,需核实'">
        提示：默认隐藏"无数据"记录（约5000+条），如需查看可在筛选中选择
      </div>
      <p v-if="!sortColumn && !isDataLoading" class="sort-default-hint">
        默认排序：参考字段未齐全的排在前，同组内按记录 id 降序（非证券代码顺序）。点击下列表头可改为按该列升序 / 降序 / 恢复默认。
      </p>

      <div class="table-shell" v-if="!isDataLoading">
        <div class="table-container">
        <!-- 精简：聚焦审核 -->
        <table v-show="viewMode === 'compact'" class="data-table">
          <thead>
            <tr>
              <th class="col-action">操作</th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('sec_code')">
                <span class="th-sort-inner">{{ colTitles.sec_code }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'sec_code' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'sec_code' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('company_name')">
                <span class="th-sort-inner">{{ colTitles.company_name }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'company_name' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'company_name' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('report_period')">
                <span class="th-sort-inner">{{ colTitles.report_period }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'report_period' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'report_period' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('inventory_amount')">
                <span class="th-sort-inner">{{ colTitles.inventory_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'inventory_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'inventory_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('intangible_amount')">
                <span class="th-sort-inner">{{ colTitles.intangible_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'intangible_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'intangible_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('dev_expense_amount')">
                <span class="th-sort-inner">{{ colTitles.dev_expense_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'dev_expense_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'dev_expense_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('total_amount')">
                <span class="th-sort-inner">{{ colTitles.total_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'total_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'total_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('entry_subject')">
                <span class="th-sort-inner">{{ colTitles.entry_subject }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'entry_subject' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'entry_subject' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th>人工检查</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in results" :key="'c-' + row.id" :class="getRowClass(row)">
              <td class="col-action">
                <button class="btn btn-xs" @click="showEditDialog(row)">编辑</button>
              </td>
              <td>{{ row.sec_code }}</td>
              <td>{{ row.company_name }}</td>
              <td>{{ row.report_period }}</td>
              <td class="editable" @click="showEditDialog(row)">{{ formatAmount(row.inventory_amount) }}</td>
              <td class="editable" @click="showEditDialog(row)">{{ formatAmount(row.intangible_amount) }}</td>
              <td class="editable" @click="showEditDialog(row)">{{ formatAmount(row.dev_expense_amount) }}</td>
              <td>{{ formatAmount(row.total_amount) }}</td>
              <td>{{ row.entry_subject || '-' }}</td>
              <td>
                <span :class="'check-flag ' + getCheckFlagClass(row.auto_check_flag)">{{ row.auto_check_flag }}</span>
              </td>
              <td>
                <a v-if="row.pdf_url" :href="row.pdf_url" target="_blank" class="pdf-link">打开</a>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 全列：含参考字段 -->
        <table v-show="viewMode === 'full'" class="data-table">
          <thead>
            <tr>
              <th class="col-action">操作</th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('sec_code')">
                <span class="th-sort-inner">{{ colTitles.sec_code }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'sec_code' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'sec_code' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('company_name')">
                <span class="th-sort-inner">{{ colTitles.company_name }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'company_name' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'company_name' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('report_period')">
                <span class="th-sort-inner">{{ colTitles.report_period }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'report_period' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'report_period' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('inventory_amount')">
                <span class="th-sort-inner">{{ colTitles.inventory_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'inventory_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'inventory_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('intangible_amount')">
                <span class="th-sort-inner">{{ colTitles.intangible_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'intangible_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'intangible_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('dev_expense_amount')">
                <span class="th-sort-inner">{{ colTitles.dev_expense_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'dev_expense_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'dev_expense_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('total_amount')">
                <span class="th-sort-inner">{{ colTitles.total_amount }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'total_amount' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'total_amount' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th class="th-sortable" scope="col" title="点击：升序 → 降序 → 恢复默认" @click="cycleSort('entry_subject')">
                <span class="th-sort-inner">{{ colTitles.entry_subject }}<span class="sort-glyphs" aria-hidden="true"><span class="g-up" :class="{ on: sortColumn === 'entry_subject' && sortDirection === 'asc' }">▲</span><span class="g-dn" :class="{ on: sortColumn === 'entry_subject' && sortDirection === 'desc' }">▼</span></span></span>
              </th>
              <th>人工检查</th>
              <th>省份</th>
              <th>行业</th>
              <th>实控人</th>
              <th>市值(亿元)</th>
              <th>市值规模</th>
              <th>资产占比</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in results" :key="'f-' + row.id" :class="getRowClass(row)">
              <td class="col-action">
                <button class="btn btn-xs" @click="showEditDialog(row)">编辑</button>
              </td>
              <td>{{ row.sec_code }}</td>
              <td>{{ row.company_name }}</td>
              <td>{{ row.report_period }}</td>
              <td class="editable" @click="showEditDialog(row)">{{ formatAmount(row.inventory_amount) }}</td>
              <td class="editable" @click="showEditDialog(row)">{{ formatAmount(row.intangible_amount) }}</td>
              <td class="editable" @click="showEditDialog(row)">{{ formatAmount(row.dev_expense_amount) }}</td>
              <td>{{ formatAmount(row.total_amount) }}</td>
              <td>{{ row.entry_subject || '-' }}</td>
              <td>
                <span :class="'check-flag ' + getCheckFlagClass(row.auto_check_flag)">{{ row.auto_check_flag }}</span>
              </td>
              <td :class="getFieldClass(row.province, row.province_from_ref)">
                {{ row.province || '-' }}
                <span v-if="row.province_from_ref" class="ref-tag">参考</span>
              </td>
              <td :class="getFieldClass(row.industry, row.industry_from_ref)">
                {{ row.industry || '-' }}
                <span v-if="row.industry_from_ref" class="ref-tag">参考</span>
              </td>
              <td :class="getFieldClass(row.controller_type, row.controller_from_ref)">
                {{ row.controller_type || '-' }}
                <span v-if="row.controller_from_ref" class="ref-tag">参考</span>
              </td>
              <td :class="getFieldClass(row.market_value, row.market_value_from_ref)">
                {{ row.market_value ? formatMarketValue(row.market_value) : '-' }}
                <span v-if="row.market_value_from_ref" class="ref-tag">参考</span>
              </td>
              <td class="computed-cell">{{ formatMarketScaleCell(row) }}</td>
              <td class="computed-cell">{{ formatAssetRatioCell(row) }}</td>
              <td>
                <a v-if="row.pdf_url" :href="row.pdf_url" target="_blank" class="pdf-link">打开</a>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <div v-else class="loading-state">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>

      <div class="pagination" v-if="totalResults > 0">
        <span>共 {{ totalResults }} 条 | </span>
        <span v-if="resultStats">
          有数据：{{ resultStats.has_data_count || 0 }} |
          需核实：{{ resultStats.need_check_count || 0 }} |
          无数据：{{ resultStats.no_data_count || 0 }}
        </span>
        <div class="page-controls">
          <button :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">上一页</button>
          <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
          <button :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">下一页</button>
        </div>
      </div>

      <div class="data-actions">
        <button class="btn btn-secondary" @click="exportResults">导出Excel</button>
      </div>

      <transition name="modal-fade">
        <div class="modal" v-if="showEditModal" @mousedown.self="showEditModal = false">
          <div class="modal-content edit-modal">
            <div class="modal-header">
              <h3>编辑数据</h3>
              <p class="modal-subtitle">{{ editingRow?.company_name }} ({{ editingRow?.sec_code }})</p>
            </div>
            <div class="form-section-label">财务数据</div>
            <div class="form-grid">
              <div class="form-group">
                <label>存货(万元)</label>
                <input type="number" v-model.number="editForm.inventory_amount" step="0.01" />
              </div>
              <div class="form-group">
                <label>无形资产(万元)</label>
                <input type="number" v-model.number="editForm.intangible_amount" step="0.01" />
              </div>
              <div class="form-group">
                <label>开发支出(万元)</label>
                <input type="number" v-model.number="editForm.dev_expense_amount" step="0.01" />
              </div>
              <div class="form-group">
                <label>人工检查</label>
                <select v-model="editForm.auto_check_flag">
                  <option value="有数据">有数据</option>
                  <option value="无数据">无数据</option>
                  <option value="需核实">需核实</option>
                </select>
              </div>
            </div>
            <div class="form-section-label">补充信息</div>
            <div class="form-grid">
              <div class="form-group">
                <label>省份</label>
                <input type="text" v-model="editForm.province" />
              </div>
              <div class="form-group">
                <label>行业</label>
                <input type="text" v-model="editForm.industry" />
              </div>
              <div class="form-group">
                <label>实控人</label>
                <input type="text" v-model="editForm.controller_type" />
              </div>
              <div class="form-group">
                <label>市值（亿元）</label>
                <input type="number" v-model.number="editForm.market_value" step="0.01" />
              </div>
            </div>
            <div class="modal-actions">
              <button class="btn btn-secondary" @click="showEditModal = false">取消</button>
              <button class="btn btn-primary" @click="saveEdit">保存</button>
            </div>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { createApiClient } from '@/services/httpSetup';
import { toastMixin } from '@/utils/toastMixin.js';
import { formatCrawlerTaskLabel } from '@/utils/crawlerTaskLabel.js';

const apiClient = createApiClient({
  baseURL: '/api/crawler'
});

const LS_LISTED_VERSION = 'crawlerDataReviewListedInfoVersion';

export default {
  name: 'CrawlerDataReview',
  mixins: [toastMixin],
  props: {
    tasks: { type: Array, default: () => [] },
    selectedTaskId: { type: [String, Number], default: '' },
    selectedTask: { type: Object, default: null },
    isLoading: { type: Boolean, default: false }
  },
  emits: ['results-loaded', 'task-change'],
  data() {
    return {
      viewMode: 'compact',
      results: [],
      totalResults: 0,
      resultStats: null,
      currentPage: 1,
      pageSize: 50,
      isDataLoading: false,
      filterAutoCheck: '有数据,需核实',
      searchKeyword: '',
      /** 表头排序：null 表示使用服务端默认（参考未齐优先 + id 降序） */
      sortColumn: null,
      sortDirection: null,
      showEditModal: false,
      editingRow: null,
      editForm: {},
      /** 必选其一：与 listedDataVersions 中某项对应，默认取列表首项（后端按版本号降序，即最新） */
      selectedListedInfoDataVersion: '',
      listedDataVersions: [],
      /** GET /listed-information/data-versions 失败（如 404），与「未导入」区分 */
      listedVersionApiFailed: false,
      /** 避免每次切换任务都弹出重复的 404 提示 */
      versionList404ToastShown: false
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalResults / this.pageSize);
    },
    isTaskParsed() {
      if (!this.selectedTask) return false;
      return !['pending', 'crawling', 'link_collected'].includes(this.selectedTask.status);
    },
    enrichMissingOnPage() {
      return this.results.filter((r) => {
        const noP = !r.province || !String(r.province).trim();
        const noI = !r.industry || !String(r.industry).trim();
        const noC = !r.controller_type || !String(r.controller_type).trim();
        const noM = r.market_value == null || r.market_value === '';
        return noP || noI || noC || noM;
      }).length;
    },
    /** 与 listed_company_information JOIN 时带出的本批快照版本（每行相同；无匹配则为空） */
    listedCompanyInfoVersionLabel() {
      if (!this.results || !this.results.length) return '';
      const hit = this.results.find(
        (r) => r.listed_company_info_version != null && String(r.listed_company_info_version).trim() !== ''
      );
      return hit ? String(hit.listed_company_info_version).trim() : '';
    },
    colTitles() {
      return {
        sec_code: '证券代码',
        company_name: '公司名称',
        report_period: '报告时间',
        inventory_amount: '存货(万元)',
        intangible_amount: '无形资产(万元)',
        dev_expense_amount: '开发支出(万元)',
        total_amount: '入表总额(万元)',
        entry_subject: '入表科目'
      };
    }
  },
  watch: {
    selectedTaskId: {
      async handler(newVal) {
        if (newVal) {
          this.currentPage = 1;
          this.sortColumn = null;
          this.sortDirection = null;
          await this.loadListedDataVersions();
          this.loadResults();
        } else {
          this.results = [];
          this.totalResults = 0;
          this.sortColumn = null;
          this.sortDirection = null;
          this.listedDataVersions = [];
          this.selectedListedInfoDataVersion = '';
          this.listedVersionApiFailed = false;
        }
      },
      immediate: true
    }
  },
  methods: {
    formatCrawlerTaskLabel,

    cycleSort(key) {
      if (this.sortColumn !== key) {
        this.sortColumn = key;
        this.sortDirection = 'asc';
      } else if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      } else {
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
      this.loadResults();
    },
    exportResults() {
      if (!this.selectedTaskId) return;
      const params = new URLSearchParams({
        taskId: this.selectedTaskId,
        enrich: 'true'
      });
      if (this.selectedListedInfoDataVersion) {
        params.set('listedInfoDataVersion', this.selectedListedInfoDataVersion);
      }
      if (this.filterAutoCheck) params.set('autoCheckFlag', this.filterAutoCheck);
      if (this.searchKeyword) params.set('searchKeyword', this.searchKeyword);
      if (this.sortColumn && this.sortDirection) {
        params.set('sortBy', this.sortColumn);
        params.set('sortOrder', this.sortDirection);
      }
      window.open(`/api/crawler/results/export?${params.toString()}`);
    },
    getTaskStatusText(status) {
      const statusMap = {
        pending: '等待中',
        crawling: '爬取链接中',
        link_collected: '链接已收集',
        parsing: '解析PDF中',
        parse_queued: '解析排队中',
        completed: '已完成',
        failed: '失败',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    },
    onListedVersionChange() {
      try {
        localStorage.setItem(LS_LISTED_VERSION, this.selectedListedInfoDataVersion);
      } catch (e) {
        /* ignore */
      }
      this.currentPage = 1;
      this.loadResults();
    },
    async loadListedDataVersions() {
      this.listedVersionApiFailed = false;
      try {
        const { data } = await apiClient.get('/listed-information/data-versions');
        this.versionList404ToastShown = false;
        this.listedDataVersions = data.versions || [];
        let prefer = '';
        try {
          prefer = localStorage.getItem(LS_LISTED_VERSION) || '';
        } catch (e) {
          prefer = '';
        }
        if (this.listedDataVersions.length === 0) {
          this.selectedListedInfoDataVersion = '';
          return;
        }
        if (prefer && this.listedDataVersions.includes(prefer)) {
          this.selectedListedInfoDataVersion = prefer;
        } else {
          this.selectedListedInfoDataVersion = this.listedDataVersions[0];
        }
      } catch (e) {
        console.error('loadListedDataVersions', e);
        this.listedVersionApiFailed = true;
        this.listedDataVersions = [];
        this.selectedListedInfoDataVersion = '';
        const msg = e.response?.data?.message || e.response?.statusText || e.message || '';
        if (e.response?.status === 404) {
          if (!this.versionList404ToastShown) {
            this.versionList404ToastShown = true;
            this.showToast('参考表「版本列表」接口 404：请重启后端 node（server.js）后再试。本页加载后会尽量根据数据推断版本。', 'warning');
          }
        } else {
          this.showToast('参考表版本列表加载失败：' + msg, 'error');
        }
      }
    },
    /**
     * 当 /data-versions 失败或返回空而 enrich 仍有 listed_company_info_version 时，用本页结果补全下拉选项
     */
    mergeListedVersionsFromResults() {
      const fromRows = [
        ...new Set(
          (this.results || [])
            .map((r) => r.listed_company_info_version)
            .filter((v) => v != null && String(v).trim() !== '')
            .map((v) => String(v).trim())
        )
      ];
      if (!fromRows.length) return;
      fromRows.sort((a, b) => String(b).localeCompare(String(a), 'zh-CN'));
      const prev = this.listedDataVersions || [];
      const merged = [...new Set([...prev, ...fromRows])].sort((a, b) =>
        String(b).localeCompare(String(a), 'zh-CN')
      );
      this.listedDataVersions = merged;
      let prefer = '';
      try {
        prefer = localStorage.getItem(LS_LISTED_VERSION) || '';
      } catch (e) {
        prefer = '';
      }
      if (prefer && merged.includes(prefer)) {
        this.selectedListedInfoDataVersion = prefer;
      } else if (!this.selectedListedInfoDataVersion || !merged.includes(this.selectedListedInfoDataVersion)) {
        this.selectedListedInfoDataVersion = merged[0];
      }
    },
    async loadResults() {
      if (!this.selectedTaskId || !this.isTaskParsed) return;

      this.isDataLoading = true;
      try {
        const params = {
          taskId: this.selectedTaskId,
          page: this.currentPage,
          pageSize: this.pageSize,
          enrich: 'true'
        };
        if (this.selectedListedInfoDataVersion) {
          params.listedInfoDataVersion = this.selectedListedInfoDataVersion;
        }
        if (this.filterAutoCheck) params.autoCheckFlag = this.filterAutoCheck;
        if (this.searchKeyword) params.searchKeyword = this.searchKeyword;
        if (this.sortColumn && this.sortDirection) {
          params.sortBy = this.sortColumn;
          params.sortOrder = this.sortDirection;
        }

        const res = await apiClient.get('/results', { params });
        this.results = res.data.data || [];
        this.totalResults = res.data.total || 0;
        this.resultStats = res.data.stats || null;
        this.$emit('results-loaded', this.resultStats);
        this.mergeListedVersionsFromResults();
      } catch (err) {
        console.error('加载结果失败:', err);
        this.showToast('加载数据失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isDataLoading = false;
      }
    },
    changePage(page) {
      this.currentPage = page;
      this.loadResults();
    },
    getRowClass(row) {
      if (row.auto_check_flag === '需核实') return 'row-warning';
      if (row.auto_check_flag === '无数据') return 'row-muted';
      return '';
    },
    getCheckFlagClass(flag) {
      if (flag === '有数据') return 'flag-success';
      if (flag === '需核实') return 'flag-warning';
      return 'flag-muted';
    },
    formatAmount(val) {
      if (val === null || val === undefined) return '0';
      const num = parseFloat(val);
      if (isNaN(num)) return '0';
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    formatMarketValue(val) {
      const num = parseFloat(val);
      if (isNaN(num)) return '-';
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getFieldClass(value, fromRef) {
      if (!value) return 'missing';
      if (fromRef) return 'from-ref';
      return '';
    },
    getMarketScale(marketValue) {
      if (!marketValue) return '-';
      const v = parseFloat(marketValue);
      if (isNaN(v)) return '-';
      if (v < 50) return '50亿以下';
      if (v < 100) return '50-100亿';
      if (v < 500) return '100-500亿';
      if (v < 1000) return '500-1000亿';
      if (v < 5000) return '1000-5000亿';
      if (v < 10000) return '5000-1万亿';
      return '1万亿以上';
    },
    getAssetRatio(totalAmount, marketValue) {
      if (!totalAmount || !marketValue) return '-';
      const total = parseFloat(totalAmount);
      const market = parseFloat(marketValue);
      if (isNaN(total) || isNaN(market) || market === 0) return '-';
      const ratio = total / (market * 10000) * 100;
      return ratio.toFixed(4) + '%';
    },
    formatMarketScaleCell(row) {
      if (row.market_scale) return row.market_scale;
      return this.getMarketScale(row.market_value);
    },
    formatAssetRatioCell(row) {
      if (row.asset_ratio != null && row.asset_ratio !== '') {
        const n = parseFloat(row.asset_ratio);
        if (!isNaN(n)) return n.toFixed(4) + '%';
      }
      return this.getAssetRatio(row.total_amount, row.market_value);
    },
    showEditDialog(row) {
      this.editingRow = row;
      this.editForm = {
        inventory_amount: row.inventory_amount,
        intangible_amount: row.intangible_amount,
        dev_expense_amount: row.dev_expense_amount,
        province: row.province,
        industry: row.industry,
        controller_type: row.controller_type,
        market_value: row.market_value,
        auto_check_flag: row.auto_check_flag
      };
      this.showEditModal = true;
    },
    async saveEdit() {
      try {
        await apiClient.put(`/results/${this.editingRow.id}`, this.editForm);
        this.showEditModal = false;
        this.showToast('保存成功', 'success');
        await this.loadResults();
      } catch (err) {
        this.showToast('保存失败: ' + (err.response?.data?.error || err.message), 'error');
      }
    }
  }
};
</script>

<style scoped>
.data-review {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-selector-bar {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.task-selector {
  display: flex;
  align-items: center;
  gap: 12px;
}

.task-selector select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 300px;
}

.task-selector select.select-empty {
  color: #999;
}

.task-stats {
  color: #666;
  font-size: 13px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state .empty-icon {
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

.enrich-alert {
  background: #fff7e6;
  border: 1px solid #faad14;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #614700;
}

.listed-version-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #2e3968;
  background: #f0f3fa;
  border: 1px solid #d6dce8;
  border-radius: 6px;
}
.listed-version-label {
  font-weight: 500;
}
.listed-version-select {
  padding: 4px 10px;
  border: 1px solid #c0c4cc;
  border-radius: 4px;
  min-width: 220px;
  font-size: 13px;
  background: #fff;
}
.listed-version-hint {
  color: #606266;
  font-size: 13px;
}
.listed-version-hint strong {
  color: #2e3968;
}
.listed-version-none {
  color: #909399;
  font-weight: 400;
}
.listed-version-warn {
  color: #b45309;
  font-size: 13px;
  max-width: 52rem;
  line-height: 1.45;
}
.listed-version-warn strong {
  color: #92400e;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.section-header h3 {
  margin: 0;
  color: #2e3968;
}

.view-toggle {
  display: inline-flex;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  overflow: hidden;
}

.btn-toggle {
  padding: 6px 14px;
  font-size: 13px;
  border: none;
  background: #fff;
  color: #2e3968;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.btn-toggle + .btn-toggle {
  border-left: 1px solid #d0d5dd;
}

.btn-toggle:hover {
  background: #f0f4fc;
}

.btn-toggle.active {
  background: #2e3968;
  color: #fff;
}

.filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filters select,
.search-input {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.search-input {
  width: 200px;
}

.data-tip {
  background: #fff3cd;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
}

.sort-default-hint {
  font-size: 12px;
  color: #606266;
  margin: 0 0 12px;
  line-height: 1.55;
}

.th-sortable {
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
}
.th-sortable:hover {
  background: #e8ecf5 !important;
}
.th-sort-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
}
.sort-glyphs {
  display: inline-flex;
  flex-direction: column;
  line-height: 0.65;
  font-size: 9px;
  opacity: 0.38;
  flex-shrink: 0;
}
.sort-glyphs .g-up,
.sort-glyphs .g-dn {
  display: block;
}
.sort-glyphs .on {
  opacity: 1;
  color: #2e3968;
  font-weight: 700;
}

/* 表格区限制高度，横向滚动条出现在可视区域底部（仅保留一条） */
.table-shell {
  margin-bottom: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fff;
}

/* 表格可视区高度：增大左侧数字(72vh)或减小减去的 px，框会更高 */
.table-container {
  overflow: auto;
  max-height: min(92vh, calc(200vh - 260px));
  -webkit-overflow-scrolling: touch;
}

.data-table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
  white-space: nowrap;
}

.data-table th,
.data-table td {
  border: 1px solid #ccc;
  padding: 6px 10px;
  text-align: left;
  color: #2e3968;
  vertical-align: middle;
  word-break: break-word;
  white-space: normal;
}

.data-table th {
  background-color: #f4f4f4;
  font-weight: 600;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 1px 0 #ccc;
}

.data-table td.editable {
  cursor: pointer;
  background: #fafafa;
}

.data-table td.editable:hover {
  background: #e8f4ff;
}

.col-action {
  width: 50px;
  min-width: 50px;
  text-align: center;
  white-space: nowrap;
}

.row-warning {
  background: #fff3cd;
}

.row-muted {
  background: #f8f9fa;
  color: #999;
}

.check-flag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.flag-success {
  background: #d4edda;
  color: #155724;
}

.flag-warning {
  background: #fff3cd;
  color: #856404;
}

.flag-muted {
  background: #f8f9fa;
  color: #6c757d;
}

.missing {
  color: #dc3545;
}

.from-ref {
  color: #0d6efd;
}

.ref-tag {
  display: inline-block;
  font-size: 10px;
  padding: 0 4px;
  margin-left: 3px;
  background: #e7f1ff;
  color: #0d6efd;
  border-radius: 3px;
  vertical-align: middle;
}

.computed-cell {
  color: #6c757d;
  font-style: italic;
}

.pdf-link {
  color: #007bff;
  text-decoration: none;
}

.pdf-link:hover {
  text-decoration: underline;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #e0e0e0;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-controls button {
  padding: 8px 16px;
  border: 1px solid transparent;
  background-color: #2e3968;
  color: white;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-controls button:hover:not(:disabled) {
  background-color: white;
  color: #2e3968;
  border-color: #2e3968;
}

.page-controls button:disabled {
  background-color: #eef0f6;
  color: #c0c4cc;
  border-color: #dcdfe6;
  opacity: 0.7;
  cursor: not-allowed;
}

.data-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

.btn {
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn:disabled {
  background-color: #eef0f6;
  color: #c0c4cc;
  border-color: #dcdfe6;
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #2e3968;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background-color: white;
  color: #2e3968;
  border-color: #2e3968;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
  border-color: #ccc;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #2e3968;
  color: white;
  border-color: #2e3968;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 13px;
}

.btn-xs {
  padding: 2px 8px;
  font-size: 12px;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-content.edit-modal {
  max-width: 640px;
}

.modal-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #2e3968;
}

.modal-header h3 {
  margin: 0 0 4px;
  color: #2e3968;
  font-size: 18px;
  font-weight: 700;
}

.modal-subtitle {
  margin: 0;
  color: #2e3968;
  font-size: 13px;
  opacity: 0.7;
}

.form-section-label {
  color: #2e3968;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 16px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.form-section-label:first-of-type {
  margin-top: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #2e3968;
  font-size: 13px;
}

.form-group input,
.form-group select {
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  width: 100%;
  box-sizing: border-box;
  font-size: 13px;
  color: #2e3968;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #2e3968;
  box-shadow: 0 0 0 3px rgba(46, 57, 104, 0.1);
}

.modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #2e3968;
  gap: 12px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(46, 57, 104, 0.15);
  border-top-color: #2e3968;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 9999;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 500px;
}

.toast-success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast-error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.toast-warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.toast-fade-enter,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}

.modal-fade-enter-active {
  transition: opacity 0.25s ease;
}

.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-content {
  animation: modal-scale-in 0.25s ease;
}

.modal-fade-leave-active .modal-content {
  animation: modal-scale-out 0.2s ease;
}

@keyframes modal-scale-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modal-scale-out {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
}
</style>
